import { z } from 'zod'

import { BlobSchema } from '@/shared/lib/file'

export const SendCrashlogParamsSchema = z.object({
    // base64 of a deflate-raw stream, compressed by the client — see GLOSSARY.md
    log: z.string(),
    // Multi-line for client crashes: the error, its stack and the build versions.
    message: z.string(),
    // JSON. The crashlog service indexes the `error` and `env.browser` paths, so their shape is a
    // data contract — see GLOSSARY.md.
    crashInfo: z.string(),
    // Comma separated, lowercased downstream and stored as a list.
    tags: z.string(),
})

export type SendCrashlogParams = z.infer<typeof SendCrashlogParamsSchema>

// Structured counterpart of the legacy `crashInfo` JSON string: the Rust client builds this object
// and the portal serializes it. `error.type` and `error.position` together replace the first line of
// the legacy `message`, `error.message` replaces the rest.
export const CrashInfoSchema = z.object({
    // Tags the payload shape, never compared — see the wrapper skew note in GLOSSARY.md.
    apiVersion: z.string(),
    error: z.object({
        // Pipe-joined identity, e.g. `Game|Server|ServerCrash`. Indexed by the crashlog service.
        type: z.string(),
        // Source location of the crash, e.g. `ServerRequest.rs:54`.
        position: z.string(),
        message: z.string(),
        // Free-form diagnostics under the error: cause chain, location list, spantrace.
        scope: z.record(z.string(), z.unknown()),
        // Unix milliseconds.
        time: z.number(),
    }),
})

export type CrashInfo = z.infer<typeof CrashInfoSchema>

export const SendCrashlogV2ParamsSchema = z.object({
    // The client log as a zlib stream — the encoding is part of this method's contract, the client
    // sends nothing else. Null when the client failed to compress it; the report is still worth
    // sending without the log. Reaches the crashlog service as its `file` field.
    logFile: BlobSchema.nullable(),
    // Preserve diagnostics from newer clients even when they no longer match CrashInfoSchema.
    crashInfo: z.record(z.string(), z.unknown()),
    // Comma separated, lowercased downstream and stored as a list.
    tags: z.string(),
})

export type SendCrashlogV2Params = z.infer<typeof SendCrashlogV2ParamsSchema>
